import React, { useState } from 'react';
import T from 'prop-types';
import cx from 'classnames';

import SmartChart from '../../../../components/Chart/SmartChart';
import { numberToHumanChart } from '../../../../utils/formatter';

import {
  unifiedDownloadsChartConfig,
  unifiedRevenueChartConfig,
  UNIFIED_DOWNLOADS_COLOR,
  PAID_COLORS,
  IAP_COLORS,
} from './chart-configs';

const legendAbbr = (value) =>
  numberToHumanChart(value, { precision: 1, strip_insignificant_zeros: true });

const buildAvg = (dates, avgValue) => {
  if (!dates || dates.length === 0) return [];
  return [
    [dates[0], avgValue],
    [dates[dates.length - 1], avgValue],
  ];
};

const zipDates = (dates = [], values = []) => dates.map((ts, i) => [ts, values[i] ?? 0]);

const DownloadsTab = ({ data }) => {
  const dlSeries = Array.isArray(data.downloads?.[0])
    ? data.downloads
    : zipDates(data.dates, data.downloads);

  const series = {
    downloads: dlSeries,
    avg: buildAvg(
      dlSeries.map(([ts]) => ts),
      data.avg?.downloads || 0
    ),
  };

  const metaSeriesData = {
    downloads: { name: 'Downloads', type: 'spline', color: UNIFIED_DOWNLOADS_COLOR },
    avg: {
      name: `30 Day Average (${legendAbbr(data.avg?.downloads || 0)})`,
      type: 'spline',
      color: UNIFIED_DOWNLOADS_COLOR,
      dashStyle: 'Dash',
      lineWidth: 1,
      enableMouseTracking: false,
    },
  };

  return (
    <SmartChart
      series={series}
      metaSeriesData={metaSeriesData}
      config={unifiedDownloadsChartConfig}
    />
  );
};

const RevenueTab = ({ data }) => {
  const dates = data.dates || [];
  const iap = Array.isArray(data.iap_revenue?.[0])
    ? data.iap_revenue
    : zipDates(dates, data.iap_revenue);
  const paid = Array.isArray(data.download_revenue?.[0])
    ? data.download_revenue
    : zipDates(dates, data.download_revenue);

  const series = {
    paid,
    iap,
    avg: buildAvg(
      paid.map(([ts]) => ts),
      data.avg?.total_revenue || 0
    ),
  };

  const metaSeriesData = {
    paid: {
      name: 'Paid',
      type: 'areaspline',
      color: PAID_COLORS.fill,
      lineColor: PAID_COLORS.line,
    },
    iap: { name: 'IAP', type: 'areaspline', color: IAP_COLORS.fill, lineColor: IAP_COLORS.line },
    avg: {
      name: `30 Day Average ($${legendAbbr(data.avg?.total_revenue || 0)})`,
      type: 'spline',
      color: PAID_COLORS.line,
      dashStyle: 'Dash',
      lineWidth: 1,
      enableMouseTracking: false,
    },
  };

  return (
    <SmartChart
      series={series}
      metaSeriesData={metaSeriesData}
      config={unifiedRevenueChartConfig}
    />
  );
};

const UnifiedTabsChart = ({ data }) => {
  const hasDownloads = !!(
    data.downloads &&
    (data.downloads.length || Object.keys(data.downloads).length)
  );
  const [tab, setTab] = useState(hasDownloads ? 'downloads' : 'revenue');

  return (
    <div>
      <div className="btn-group h-tab-nav-wrapper m-b">
        <button
          type="button"
          className={cx('h-tab-nav-item btn btn-sm btn-outline', tab === 'downloads' && 'active')}
          onClick={() => setTab('downloads')}
        >
          Downloads
        </button>
        <button
          type="button"
          className={cx('h-tab-nav-item btn btn-sm btn-outline', tab === 'revenue' && 'active')}
          onClick={() => setTab('revenue')}
        >
          Revenue
        </button>
      </div>
      <div className="h-tab-wrapper">
        {tab === 'downloads' ? <DownloadsTab data={data} /> : <RevenueTab data={data} />}
      </div>
    </div>
  );
};

UnifiedTabsChart.propTypes = {
  data: T.shape({
    downloads: T.arrayOf(T.array),
    total_revenue: T.arrayOf(T.array),
    iap_revenue: T.arrayOf(T.array),
    download_revenue: T.arrayOf(T.array),
    avg: T.shape({
      downloads: T.number,
      total_revenue: T.number,
    }),
    dates: T.arrayOf(T.number),
  }).isRequired,
};

export default UnifiedTabsChart;
